import React from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import map from 'lodash/map';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';

import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import Field from 'core/components/form/form.v2';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import Grid from '@material-ui/core/Grid';
import CollapsibleListItem from './collapsible-list-item';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import useStyles from './sub-risk.styles';

const validationSchema = () =>
  Yup.object().shape({
    category: Yup.string().required('Type of item is required'),
    limit: Yup.number().required('Limit is required'),
    coinsurance: Yup.string().required('Coinsurance is required'),
    days: Yup.string().required('Period of loss extension is required'),
    description: Yup.string().nullable()
  });

const FarmIncomeCoverageForm = ({ fieldName, disabled }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  const scheduleCoverages = getIn(values, fieldName) || [];
  const farmIncomeItems =
    scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.INCOME)?.items || [];

  // Auto-expand if there are any scheduled farm income items
  const hasScheduledItems = farmIncomeItems.length > 0;

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Farm Income Coverage</div>}
        initialOpen={hasScheduledItems}
      >
        <FormSectionWrapper
          sectionTitle="Farm income coverage items"
          totalLabel="items"
          totalCount={farmIncomeItems?.length || 0}
          emptyStateTitle="There are no farm income items added yet"
        >
          {/* list items */}
          {map(farmIncomeItems, (item, index) => {
            const itemCategory = lookupsJson.farm.farmIncomeItemTypes.find((type) => type.id === item.category).value;

            return (
              <CollapsibleListItem
                key={`farmIncome-${index}`}
                title={itemCategory}
                rightSideValues={[{ label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` }]}
                detailFields={[
                  { name: 'category', label: 'Type of Item', value: itemCategory, size: 6 },
                  {
                    name: 'limit',
                    label: 'Limit',
                    value: item.limit ? `$${item.limit.toLocaleString('en-US')}` : null,
                    size: 6
                  },
                  { name: 'coinsurance', label: 'Coinsurance', value: item.extendedDetails?.coinsurance, size: 6 },
                  {
                    name: 'days',
                    label: 'Period of Loss Extension',
                    value: item.extendedDetails?.days,
                    size: 6
                  },
                  { name: 'description', label: 'Description', value: item.description || 'Not provided', size: 12 }
                ]}
                onDelete={() => {
                  const newItems = farmIncomeItems.filter((_, checkIndex) => checkIndex !== index);
                  const currentScheduleCoverages =
                    getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.INCOME) ||
                    [];
                  setFieldValue(`${fieldName}`, [
                    ...currentScheduleCoverages,
                    {
                      coverageType: FarmScheduleCoverageType.INCOME,
                      items: newItems
                    }
                  ]);
                  setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.INCOME}`);
                }}
                disabled={disabled}
                keyPrefix={`farmIncome-${index}`}
              />
            );
          })}

          {/* add form */}
          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                category: undefined,
                limit: undefined,
                coinsurance: undefined,
                days: undefined,
                description: undefined
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema()}
              onSubmit={async (val, { resetForm }) => {
                const parentErrors = await validateForm();
                if (Object.keys(parentErrors).length > 0) {
                  setErrors(parentErrors);
                  setTouched(setTouchedFromErrors(parentErrors));
                  setShowValidationErrorsModal(true);
                  return;
                }

                const item = val;
                Object.keys(item).forEach((key) => {
                  if (item[key] === '') {
                    item[key] = null;
                  }
                });

                const newItem = {
                  category: item.category,
                  description: item.description,
                  limit: item.limit,
                  extendedDetails: {
                    coinsurance: item.coinsurance,
                    days: item.days
                  }
                };

                const currentScheduleCoverages =
                  getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.INCOME) ||
                  [];
                const currentFarmIncomeItems =
                  getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.INCOME)
                    ?.items || [];

                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.INCOME,
                    items: [...currentFarmIncomeItems, newItem]
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.INCOME}`);

                resetForm();
              }}
            >
              {({ submitForm }) => {
                return (
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Field
                      xs={6}
                      type="select"
                      id="category"
                      name="category"
                      mode="dark"
                      label="Type of item"
                      options={lookupsJson.farm.farmIncomeItemTypes}
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="numeric"
                      id="limit"
                      name="limit"
                      mode="dark"
                      label="Limit"
                      format={{ thousandSeparator: true, prefix: '$' }}
                      disabled={disabled}
                    />

                    <Field
                      xs={6}
                      type="select"
                      id="coinsurance"
                      name="coinsurance"
                      mode="dark"
                      label="Coinsurance"
                      options={lookupsJson.farm.farmIncomeCoinsurance}
                      disabled={disabled}
                    />
                    <Field
                      xs={6}
                      type="select"
                      id="days"
                      name="days"
                      mode="dark"
                      label="Period of loss extension (in days)"
                      options={lookupsJson.farm.farmIncomeLossExtensionDays}
                      disabled={disabled}
                    />

                    <Field
                      xs={12}
                      type="textarea"
                      id="description"
                      name="description"
                      mode="dark"
                      label="Description"
                      disabled={disabled}
                    />

                    <Grid container alignItems="center" className={classes.singleBottom}>
                      <Button
                        type="button"
                        mode="big"
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        disabled={disabled}
                        onClick={submitForm}
                      >
                        Add Item
                      </Button>
                    </Grid>
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </>
  );
};

FarmIncomeCoverageForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};

FarmIncomeCoverageForm.defaultProps = {
  disabled: false
};

export default observer(FarmIncomeCoverageForm);
